import { useQuery } from "@tanstack/react-query";
import { Link2 } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import { formatMinutes } from "../lib/domain";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/ui";

interface CrossListGroup {
  groupId: string;
  crns: string[];
  courses: string[];
  totalMaxEnrollment: number;
  totalEnrollment: number;
  roomCapacity: number;
  utilization: number;
  campus: string;
  room: string;
  dayCode: string;
  startMinutes: number;
  endMinutes: number;
}

export function CrossListsPage() {
  const { snapshotTermId, departmentId } = useAppContext();
  const query = useQuery({
    queryKey: ["cross-lists", snapshotTermId, departmentId],
    queryFn: () => apiFetch<CrossListGroup[]>(`/cross-lists${queryString({ snapshotTermId, department: departmentId })}`),
    enabled: Boolean(snapshotTermId),
  });
  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;
  return (
    <>
      <PageHeader title="Cross-List Workspace" description="Sections sharing one physical booking are consolidated to prevent false room-conflict interpretation." />
      {query.data?.length === 0 ? (
        <EmptyState title="No cross-listed groups" description="The selected snapshot does not contain a Cross List Group Identifier." />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {query.data?.map((group) => (
            <article key={group.groupId} className="panel p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-brand-600">Group {group.groupId}</p>
                  <h2 className="mt-1 text-lg font-black text-slate-950">{group.courses.join(" · ")}</h2>
                </div>
                <Link2 className="h-5 w-5 text-brand-500" />
              </div>
              <p className="mt-3 text-xs text-slate-500">CRNs {group.crns.join(", ")}</p>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div><p className="text-[10px] font-bold uppercase text-slate-400">Location</p><p className="mt-1 text-sm font-semibold">{group.campus} / {group.room}</p></div>
                <div><p className="text-[10px] font-bold uppercase text-slate-400">Meeting</p><p className="mt-1 text-sm font-semibold">{group.dayCode} {formatMinutes(group.startMinutes)}–{formatMinutes(group.endMinutes)}</p></div>
                <div><p className="text-[10px] font-bold uppercase text-slate-400">Group Max</p><p className="mt-1 text-sm font-semibold">{group.totalMaxEnrollment} / {group.roomCapacity}</p></div>
                <div><p className="text-[10px] font-bold uppercase text-slate-400">Utilization</p><p className={`mt-1 text-sm font-black ${group.utilization > 1 ? "text-rose-600" : "text-emerald-600"}`}>{(group.utilization * 100).toFixed(1)}%</p></div>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
